import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Autocomplete, ClickAwayListener, styled, TextField, Tooltip, tooltipClasses, useMediaQuery } from '@mui/material';
import { DatePicker, LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CustomDatePickerFilterProps } from './CustomDatePickerFilter.props';
import { StyledTextfield } from './CustomDatePickerFilter.styles';
import { ScreenSize, SizeProps } from '../../../@types';
import { AUTOCOMPLET_DROPDOWN_ITEMS, RELATIVE_DATE_RANGES } from '../../../constants/attendanceReport';
import { formatDateOnly } from '../../../utils/common';

export const StyledTooltip = styled(({ className, ...props }: any) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme, tooltipTopMargin }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    marginTop: tooltipTopMargin || 100
  },
}));

export const CustomDatePickerFilter: React.FC<CustomDatePickerFilterProps> = ({
  item,
  applyValue,
  tooltipTopMargin,
  disableRelativeTimes
}) => {
  const [value, setValue] = useState(AUTOCOMPLET_DROPDOWN_ITEMS);
  const [openTooltip, setOpenTooltip] = useState(false);
  const [openAutocomplete, setOpenAutocomplete] = useState(false);

  const isMobileView = useMediaQuery(ScreenSize.MOBILE);

  useEffect(() => {
    !disableRelativeTimes && item.value && setValue({
      label: RELATIVE_DATE_RANGES.find((dateRange) => dateRange.value === item.value)?.label || item.value,
      value: item.value
    });
  }, [item]);
    
  const handleDateChange = (newValue: dayjs.Dayjs | null) => {
    if (newValue && dayjs(newValue).isValid()) {
      const formattedValue = dayjs(newValue).format('YYYY-MM-DD');
      applyValue({ ...item, value: formattedValue });
    } else {
      applyValue({ ...item, value: null });
    }
  };

  const formattedValue = item.value && dayjs(item.value, 'YYYY-MM-DD', true).isValid() ? dayjs(item.value) : null;

  const handleCloseAll = () => {
    setOpenTooltip(false);
    setOpenAutocomplete(false);
  };

  return (
    (disableRelativeTimes ?
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          format="YYYY-MM-DD"
          views={['year', 'month', 'day']}
          showDaysOutsideCurrentMonth
          value={formattedValue}
          onChange={(newValue: dayjs.Dayjs | null) => {
            if (newValue && dayjs(newValue).isValid()) {
              const formattedValue = dayjs(newValue).format('YYYY-MM-DD');
              applyValue({ ...item, value: formattedValue });
            }
          }}
          onAccept={handleDateChange}
          slots={{
            textField: StyledTextfield
          }}
          slotProps={{
            field: {
              clearable: true
            },
            textField: {
              inputProps: {
                placeholder: 'YYYY-MM-DD',
                inputMode: 'text',
                autoComplete: 'off',
                readOnly: false
              }
            },
            actionBar: {
              actions: ['today', 'clear']
            }
          }}
        />
      </LocalizationProvider>
      :
      <ClickAwayListener onClickAway={handleCloseAll}>
        <div>
          <StyledTooltip
            tooltipTopMargin={tooltipTopMargin}
            placement={isMobileView ? 'bottom' : 'right'}
            onClose={() => setOpenTooltip(false)}
            open={openTooltip}
            disableFocusListener
            disableHoverListener
            disableTouchListener={isMobileView}
            title={
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StaticDatePicker
                  onAccept={() => {
                    value && applyValue({ ...item, value: value?.value });
                    handleCloseAll();
                  }}
                  onChange={(event: any) => {
                    !openAutocomplete && setOpenAutocomplete(true);
                    setValue({
                      label: formatDateOnly(event?.toString()),
                      value: formatDateOnly(event?.toString())
                    });
                  }}
                  onViewChange={() => !openAutocomplete && setOpenAutocomplete(true)}
                  onMonthChange={() => !openAutocomplete && setOpenAutocomplete(true)}
                  onClose={handleCloseAll}
                />
              </LocalizationProvider>
            }
          >
            <Autocomplete
              id="customdatepicker-relative-time-filters"
              freeSolo
              value={value}
              open={openAutocomplete}
              onOpen={() => setOpenAutocomplete(true)}
              onClose={() => setOpenAutocomplete(false)}
              options={RELATIVE_DATE_RANGES}
              size={SizeProps.Small}
              sx={{ minWidth: 250 }}
              renderInput={(params) => 
                <TextField
                  {...params}
                  label="Select Date"
                  slotProps={{
                    input: { ...params.InputProps }
                  }}
                  onClick={() => {
                    setOpenAutocomplete(true);
                    setOpenTooltip(true);
                  }}
                />
              }
              onChange={(obj: any, value: any) => {
                applyValue({ ...item, value: value?.value });
                handleCloseAll();
              }}
            />
          </StyledTooltip>
        </div>
      </ClickAwayListener>
    )
  );
};